// Reuse the suggestions already on screen while the user keeps typing the same
// address, instead of spending a HERE call (and rate limit) on every pause.
//
// Only kicks in once the query holds a civic number and a few street letters:
// before that the list is too broad for local filtering to be trustworthy.

/** Must match the server's HereApi::AUTOCOMPLETE_LIMIT. */
export const SUGGESTION_LIMIT = 6;

const NARROWABLE_QUERY = /^\d+\S*\s+\S{3,}/u;

function words(text) {
  return String(text || "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .split(/[\s,-]+/)
    .filter(Boolean);
}

/** Every typed word must start a word of the label ("7988 tyson oa" ~ "7988 Tyson Oaks Cir"). */
function matchesAllWords(label, query) {
  const labelWords = words(label);
  return words(query).every(word => labelWords.some(labelWord => labelWord.startsWith(word)));
}

/**
 * @param {{query:string, suggestions:Array<{label:string}>}|null} previous last HERE answer
 * @param {string} query what the user has typed now
 * @returns {{suggestions:Array, exhaustive:boolean}|null} null when HERE must be asked.
 *   `exhaustive` is true when the previous answer was not truncated at
 *   SUGGESTION_LIMIT, so no better match can be missing and HERE can be skipped.
 */
export function narrowSuggestions(previous, query) {
  if (!previous || !NARROWABLE_QUERY.test(previous.query.trim())) {
    return null;
  }
  const previousQuery = words(previous.query).join(" ");
  const currentQuery = words(query).join(" ");
  if (!currentQuery.startsWith(previousQuery)) {
    return null;
  }
  const suggestions = previous.suggestions.filter(s => matchesAllWords(s.label, query));
  if (!suggestions.length) {
    return null;
  }
  return { suggestions, exhaustive: previous.suggestions.length < SUGGESTION_LIMIT };
}

export default narrowSuggestions;
